import { defineComponent, nextTick } from "vue"
import UserIcon from "@/components/icons/UserIcon.vue"
import PinnIcon from "@/components/icons/PinnIcon.vue"
import SmileIcon from "@/components/icons/SmileIcon.vue"
import SendMessage from "@/components/icons/SendMessage.vue"
import Message from "@/components/chat/desktop/Message.vue"
import { AsyncState, ChatError, GiveawayType, UserData } from "@/store/types"
import { callApi } from "@/services/api"
import Mention from "@/components/uikit/Mention.vue"
import Button from "@/components/uikit/Button.vue"
import Arrow from "@/components/icons/Arrow.vue"
import type { EmojiExt } from "vue3-emoji-picker"
import MentionInput from "@/components/uikit/MentionInput.vue"
import NotificationBanner from "@/components/uikit/NotificationBanner.vue"
import PinnedMessages from "../desktop/PinnedMessages.vue"
import { MessageType } from "../types.js"

export default defineComponent({
  components: {
    MentionInput,
    Arrow,
    UserIcon,
    PinnIcon,
    SmileIcon,
    SendMessage,
    Message,
    Mention,
    Button,
    NotificationBanner,
    PinnedMessages,
  },
  emits: ["close"],
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      text: "",
      loading: false,
      users: [] as UserData[],
      isScrolled: false,
      showEmojiPicker: false,
      emojiToggleEl: null as HTMLElement | null,
    }
  },
  computed: {
    hasGiveawaysBanner() {
      return !!(
        this.$store.getters["giveaways/data"] as AsyncState<GiveawayType[]>
      ).data.length
    },
    user(): UserData {
      return this.$store.state.auth.user
    },
    messages() {
      return this.$store.getters["global/messages"]
    },
    chatErrors(): ChatError[] {
      return this.$store.getters["global/globalChatErrors"]
    },
  },
  watch: {
    messages: {
      handler() {
        this.scrollToBottom()
      },
      deep: true,
    },
    isOpen(newVal: boolean) {
      if (newVal) {
        this.scrollToBottom()
      } else {
        this.showEmojiPicker = false
      }
    },
  },
  created() {
    if (
      this.$route.path.includes("moderator") ||
      this.$route.path.includes("admin")
    ) {
      this.$privateSocket.emit("joinGlobalChat")
    } else {
      this.$publicSocket.emit("joinPublicGlobalChat")
    }
  },
  async mounted() {
    this.scrollToBottom(true)

    await nextTick()

    this.emojiToggleEl = (this.$refs.emojiToggle as any)?.$el as HTMLElement
  },
  unmounted() {
    this.$privateSocket.emit("leaveGlobalChat")
    this.$publicSocket.emit("leavePublicGlobalChat")
    this.$store.commit("global/CLEAR_MESSAGES")
  },
  methods: {
    isLastMessageFromSameUser(message: MessageType, index: number | string) {
      const numberIndex = index as number
      return (
        index === 0 ||
        this.messages[numberIndex - 1].user.id !== message.user.id
      )
    },
    isPrevMessageUserSame(message: MessageType, index: number | string) {
      const numberIndex = index as number
      return (
        numberIndex === this.messages.length - 1 ||
        this.messages[numberIndex + 1].user.id !== message.user.id
      )
    },
    sendMessage() {
      if (!this.text.trim()) return

      this.$privateSocket.emit("globalChatMessage", {
        user: {
          id: this.user.id,
          name: this.user.name,
          avatar: this.user.avatar,
        },
        message: this.text,
      })

      this.text = ""
      this.showEmojiPicker = false
    },
    scrollToBottom(force = false) {
      this.isScrolled = this.isScrolledToBottom()
      if (!this.isScrolled && !force) return

      const messagesContainer = this.$refs.messages as HTMLElement

      this.$nextTick(() => {
        messagesContainer.scrollTo({
          top: messagesContainer.scrollHeight,
          behavior: "auto",
        })
      })

      if (force) this.isScrolled = true
    },

    isScrolledToBottom(): boolean {
      const messagesContainer = this.$refs.messages as HTMLElement
      return messagesContainer
        ? messagesContainer.scrollHeight - messagesContainer.scrollTop <=
            messagesContainer.clientHeight + 50
        : false
    },

    async searchUsers(query: string) {
      if (!query) return []
      const users = await callApi.getUsers(query)
      return users.data.data
    },

    send() {
      if (!this.text.trim()) return
      this.text = ""
    },

    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker
    },

    onSelectEmoji(emoji: EmojiExt) {
      const mentionInput = this.$refs.mentionInput as {
        $refs: { editor: HTMLElement }
      }
      const editor = mentionInput.$refs.editor

      editor.scrollTo({
        top: editor.scrollHeight,
        behavior: "auto",
      })
      this.text += emoji.i
    },
    closeEmojiPicker() {
      this.showEmojiPicker = false
    },
  },
})
